import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getAffiliate, createAffiliate, getCommissions, getReferredSignupCount, updateAffiliatePayoutDetails } from '../services/userDataService'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'

const CHART_EARNINGS = '#3b82f6'

const CODE_REGEX = /^[a-z0-9_-]{3,20}$/

const css = `
.aff-root {
  min-height: 100vh;
  position: relative;
  background:
    radial-gradient(1000px 460px at 70% -12%, rgba(59, 130, 246, 0.12), transparent 65%),
    radial-gradient(700px 400px at 8% 20%, rgba(168, 85, 247, 0.05), transparent 60%),
    radial-gradient(800px 460px at 95% 105%, rgba(59, 130, 246, 0.05), transparent 60%),
    #010714;
}
@keyframes affFadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.aff-fade { animation: affFadeUp 0.45s ease both; }
.aff-d1 { animation-delay: 0s; }
.aff-d2 { animation-delay: 0.07s; }
.aff-d3 { animation-delay: 0.15s; }
.aff-d4 { animation-delay: 0.23s; }
.aff-aurora {
  position: fixed;
  top: -22%;
  left: 50%;
  width: 900px;
  height: 620px;
  border-radius: 50%;
  background: radial-gradient(closest-side, rgba(59, 130, 246, 0.07), transparent);
  filter: blur(60px);
  pointer-events: none;
  z-index: 0;
  animation: affAurora 38s ease-in-out infinite alternate;
}
@keyframes affAurora {
  from { transform: translate(-80%, 0) rotate(0deg) scale(1); }
  to { transform: translate(-18%, 14%) rotate(35deg) scale(1.15); }
}
.aff-range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, #3b82f6 var(--fill), rgba(255, 255, 255, 0.09) var(--fill));
  outline: none;
  cursor: pointer;
}
.aff-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  border: none;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.25);
  cursor: grab;
  transition: box-shadow 0.15s ease;
}
.aff-range::-webkit-slider-thumb:active { cursor: grabbing; box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.3); }
.aff-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  border: none;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.25);
  cursor: grab;
}
.aff-range::-moz-range-progress { background: #3b82f6; height: 4px; border-radius: 2px; }
@media (prefers-reduced-motion: reduce) {
  .aff-fade { animation: none; }
  .aff-aurora { animation: none; }
}
.aff-glass {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.018));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 24px 60px -36px rgba(0, 0, 0, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
.aff-step {
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}
.aff-step:hover {
  transform: translateY(-3px);
  border-color: rgba(59, 130, 246, 0.35);
  background: linear-gradient(180deg, rgba(59, 130, 246, 0.06), rgba(255, 255, 255, 0.02));
}
.aff-btn { transition: transform 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease; cursor: pointer; }
.aff-btn:hover { filter: brightness(1.15); transform: translateY(-1px); }
.aff-btn:active { transform: translateY(0); }
.aff-btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 8px 24px -10px rgba(59, 130, 246, 0.6);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-weight: 600;
}
.aff-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 999px;
  color: #94a3b8;
  font-size: 12.5px;
  font-weight: 500;
  padding: 7px 14px;
  white-space: nowrap;
}
.aff-input {
  width: 100%;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #f1f5f9;
  padding: 11px 14px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.aff-input:focus {
  border-color: rgba(59, 130, 246, 0.55);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}
.aff-seg {
  display: inline-flex;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 3px;
  gap: 3px;
}
.aff-seg button {
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}
.aff-seg button.on {
  background: rgba(59, 130, 246, 0.16);
  color: #60a5fa;
}
.aff-seg button:not(.on):hover { color: #94a3b8; }
.aff-kpis { display: grid; grid-template-columns: repeat(4, 1fr); }
.aff-kpi { padding: 20px 24px; min-width: 0; }
.aff-kpi + .aff-kpi { border-left: 1px solid rgba(255, 255, 255, 0.055); }
@media (max-width: 767px) {
  .aff-kpis { grid-template-columns: 1fr 1fr; }
  .aff-kpi + .aff-kpi { border-left: none; }
  .aff-kpi { border-top: 1px solid rgba(255, 255, 255, 0.055); }
  .aff-kpis .aff-kpi:nth-child(-n+2) { border-top: none; }
}
.aff-table-wrap { overflow-x: auto; margin: 6px -24px 0; padding: 0 24px; }
.aff-table { width: 100%; border-collapse: collapse; }
.aff-table th {
  text-align: left;
  color: #4d5b74;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 12px 18px 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  white-space: nowrap;
}
.aff-table td {
  color: #94a3b8;
  font-size: 13px;
  padding: 13px 18px 13px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.aff-table th:last-child, .aff-table td:last-child { padding-right: 0; }
.aff-table .num { text-align: right; }
.aff-table tbody tr { transition: background 0.15s ease; }
.aff-table tbody tr:hover { background: rgba(255, 255, 255, 0.022); }
.aff-table tbody tr:last-child td { border-bottom: none; padding-bottom: 4px; }
.aff-mono { font-family: 'Fira Code', 'Consolas', monospace; font-size: 13px; letter-spacing: -0.2px; }
.aff-grad-text {
  background: linear-gradient(120deg, #60a5fa, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}
.aff-term { display: flex; gap: 10px; align-items: flex-start; }
`

const Icon = ({ size = 18, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
)

const LinkIcon = (p) => (
  <Icon {...p}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </Icon>
)

const MegaphoneIcon = (p) => (
  <Icon {...p}>
    <path d="M3 11l18-7-4 14-5.5-3.5L8 18l-1-4.5L3 11z" />
  </Icon>
)

const BanknoteIcon = (p) => (
  <Icon {...p}>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6 12h.01M18 12h.01" />
  </Icon>
)

const CopyIcon = (p) => (
  <Icon {...p}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Icon>
)

const CheckIcon = (p) => (
  <Icon {...p}>
    <polyline points="20 6 9 17 4 12" />
  </Icon>
)

const suggestCodeFromEmail = (email) => {
  if (!email) return ''
  const prefix = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_-]/g, '')
  return prefix.slice(0, 20)
}

const formatMoney = (cents) => '$' + (cents / 100).toFixed(2)

const formatDate = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

const PILL = {
  pending: { color: '#fbbf24', bg: 'rgba(245, 158, 11, 0.09)', border: 'rgba(245, 158, 11, 0.22)' },
  payable: { color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.09)', border: 'rgba(59, 130, 246, 0.22)' },
  paid: { color: '#4ade80', bg: 'rgba(34, 197, 94, 0.09)', border: 'rgba(34, 197, 94, 0.22)' },
  void: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.09)', border: 'rgba(100, 116, 139, 0.22)' }
}

const Pill = ({ status }) => {
  const s = PILL[status] || PILL.void
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      color: s.color,
      background: s.bg,
      border: `1px solid ${s.border}`,
      borderRadius: '999px',
      padding: '3px 10px 3px 9px',
      fontSize: '10px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      lineHeight: '14px'
    }}>
      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
      {status}
    </span>
  )
}

export default function AffiliatePage() {
  const { user } = useAuth()
  const [affiliate, setAffiliate] = useState(null)
  const [commissions, setCommissions] = useState([])
  const [signupCount, setSignupCount] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [code, setCode] = useState('')
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640)
  const [payoutMethod, setPayoutMethod] = useState('paypal')
  const [payoutEmail, setPayoutEmail] = useState('')
  const [payoutBsb, setPayoutBsb] = useState('')
  const [payoutAccount, setPayoutAccount] = useState('')
  const [savingPayout, setSavingPayout] = useState(false)
  const [payoutSaved, setPayoutSaved] = useState(false)
  const [payoutError, setPayoutError] = useState('')
  const [calcRefs, setCalcRefs] = useState(5)
  const [noticeDismissed, setNoticeDismissed] = useState(() => {
    try { return localStorage.getItem('bc-aff-notice-dismissed') === 'true' } catch (e) { return false }
  })

  const dismissNotice = () => {
    setNoticeDismissed(true)
    try { localStorage.setItem('bc-aff-notice-dismissed', 'true') } catch (e) { /* ignore */ }
  }

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const loadData = useCallback(async () => {
    if (!user) {
      setLoaded(true)
      return
    }
    const { data: affiliateRow } = await getAffiliate(user.id)
    setAffiliate(affiliateRow)
    if (affiliateRow) {
      const [commissionsRes, countRes] = await Promise.all([
        getCommissions(affiliateRow.id),
        getReferredSignupCount()
      ])
      setCommissions(commissionsRes.data || [])
      setSignupCount(countRes.data || 0)
    }
    setLoaded(true)
  }, [user])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Pre-suggest a code from the email prefix once the user is known
  useEffect(() => {
    if (user?.email && !code) {
      setCode(suggestCodeFromEmail(user.email))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Prefill payout fields from the affiliate row
  useEffect(() => {
    if (affiliate) {
      setPayoutMethod(affiliate.payout_method || 'paypal')
      setPayoutEmail(affiliate.payout_email || '')
      setPayoutBsb(affiliate.payout_bsb || '')
      setPayoutAccount(affiliate.payout_account_number || '')
    }
  }, [affiliate])

  const handleJoin = async (e) => {
    e.preventDefault()
    setFormError('')

    const cleanCode = code.trim().toLowerCase()
    if (!CODE_REGEX.test(cleanCode)) {
      setFormError('Codes are 3 to 20 characters: lowercase letters, numbers, dashes or underscores.')
      return
    }

    setSubmitting(true)
    const { data, error } = await createAffiliate({
      userId: user.id,
      code: cleanCode
    })
    setSubmitting(false)

    if (error === 'code_taken') {
      setFormError('That code is taken, try another.')
      return
    }
    if (error === 'already_affiliate') {
      await loadData()
      return
    }
    if (error) {
      setFormError('Something went wrong. Please try again.')
      return
    }

    setAffiliate(data)
  }

  const handleSavePayout = async (e) => {
    e.preventDefault()
    setPayoutError('')
    setPayoutSaved(false)

    if (payoutMethod === 'paypal') {
      if (!payoutEmail.trim().includes('@')) {
        setPayoutError('Enter a valid PayPal email.')
        return
      }
    } else {
      if (!/^\d{3}-?\d{3}$/.test(payoutBsb.trim())) {
        setPayoutError('BSB should be 6 digits, like 062-000.')
        return
      }
      if (!/^\d{5,10}$/.test(payoutAccount.trim())) {
        setPayoutError('Account number should be 5 to 10 digits.')
        return
      }
    }

    setSavingPayout(true)
    const { data, error } = await updateAffiliatePayoutDetails(user.id, {
      method: payoutMethod,
      email: payoutEmail.trim(),
      bsb: payoutBsb.trim(),
      accountNumber: payoutAccount.trim()
    })
    setSavingPayout(false)

    if (error || !data) {
      setPayoutError('Could not save. Please try again.')
      return
    }
    setAffiliate(data)
    setPayoutSaved(true)
    setTimeout(() => setPayoutSaved(false), 2500)
  }

  const referralLink = affiliate ? `https://app.bookiecharity.com/?via=${affiliate.code}` : ''

  const handleCopy = async () => {
    if (!referralLink) return
    await navigator.clipboard.writeText(referralLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Derived money stats (cents)
  const now = Date.now()
  const pendingCents = commissions
    .filter(c => c.status === 'pending' && new Date(c.matures_at).getTime() > now)
    .reduce((sum, c) => sum + c.commission_cents, 0)
  const payableCents = commissions
    .filter(c => c.status === 'pending' && new Date(c.matures_at).getTime() <= now)
    .reduce((sum, c) => sum + c.commission_cents, 0)
  const paidCents = commissions
    .filter(c => c.status === 'paid')
    .reduce((sum, c) => sum + c.commission_cents, 0)

  const hasEarnings = commissions.length > 0
  const hasPayoutDetails = affiliate
    ? (affiliate.payout_method === 'bank_au'
        ? Boolean(affiliate.payout_bsb && affiliate.payout_account_number)
        : Boolean(affiliate.payout_email))
    : false
  const needsPayoutDetails = affiliate && hasEarnings && !hasPayoutDetails

  const displayStatus = (c) =>
    c.status === 'pending' && new Date(c.matures_at).getTime() <= now ? 'payable' : c.status

  const labelStyle = {
    display: 'block',
    color: '#7c8db0',
    fontSize: '12px',
    fontWeight: 500,
    marginBottom: '7px'
  }

  const kpis = affiliate ? [
    { label: 'Referred signups', value: signupCount.toString(), dot: '#64748b' },
    { label: 'Pending', value: formatMoney(pendingCents), dot: '#f59e0b' },
    { label: 'Payable', value: formatMoney(payableCents), dot: '#3b82f6' },
    { label: 'Paid out', value: formatMoney(paidCents), dot: '#22c55e' }
  ] : []

  const isActiveAffiliate = Boolean(user && affiliate && affiliate.status !== 'disabled')

  // Monthly earnings, last 12 months (void rows excluded)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const chartBuckets = []
  if (isActiveAffiliate) {
    const chartMap = {}
    const today = new Date()
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
      const bucket = {
        key: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.getMonth() === 0 ? `Jan '${String(d.getFullYear()).slice(2)}` : monthNames[d.getMonth()],
        earnings: 0
      }
      chartBuckets.push(bucket)
      chartMap[bucket.key] = bucket
    }
    commissions.forEach((c) => {
      if (c.status === 'void') return
      const d = new Date(c.created_at)
      const bucket = chartMap[`${d.getFullYear()}-${d.getMonth()}`]
      if (bucket) bucket.earnings += c.commission_cents / 100
    })
  }
  const hasChartData = chartBuckets.some((b) => b.earnings > 0)

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    return (
      <div style={{
        background: '#141d33',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '9px',
        padding: '10px 13px',
        boxShadow: '0 12px 30px -12px rgba(0, 0, 0, 0.6)'
      }}>
        <p style={{ color: '#5b6a85', margin: '0 0 5px 0', fontSize: '11px', fontWeight: 500 }}>{label}</p>
        <p style={{ color: '#e2e8f0', margin: 0, fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '7px', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: CHART_EARNINGS, flexShrink: 0 }} />
          Earned&nbsp; ${payload[0].value.toFixed(2)}
        </p>
      </div>
    )
  }

  const steps = [
    {
      icon: <LinkIcon color="#60a5fa" />,
      title: 'Get your link',
      text: 'Pick a referral code and get a link you can share anywhere. Joining is instant and free.'
    },
    {
      icon: <MegaphoneIcon color="#a78bfa" />,
      title: 'Share it',
      text: 'Put it in your content, socials or bio. Anyone who clicks is tracked for 90 days.'
    },
    {
      icon: <BanknoteIcon color="#4ade80" />,
      title: 'Get paid',
      text: 'Earn 30% of every payment they make, for as long as they stay subscribed. Paid around the 15th of each month via PayPal or Australian bank transfer.'
    }
  ]

  const terms = [
    'Commission is 30% of every payment a referred customer makes, for the lifetime of their subscription.',
    'Attribution lasts 90 days from the last click on your link. Last click wins.',
    'Commissions become payable 30 days after the payment (covers refunds and chargebacks). Refunded payments are voided.',
    'Payouts are processed around the 15th of each month via PayPal or Australian bank transfer, with a $25 AUD minimum. Smaller balances roll over.',
    'Self-referral is not allowed. Referring your own account earns nothing.',
    'Disclose your affiliate relationship when promoting Bookie Charity, as required under Australian Consumer Law.',
    'Fraud or abuse (fake accounts, misleading promotion, bidding on our brand terms) voids commissions and disables the account.'
  ]

  return (
    <div className="aff-root" style={{ padding: isMobile ? '36px 16px 64px' : '56px 24px 88px' }}>
      <style>{css}</style>
      <div className="aff-aurora" />
      <div style={{ maxWidth: '960px', margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Migration notice (PromoteKit cutover, remove after a couple of months) */}
        {!noticeDismissed && (
          <div className="aff-glass aff-fade aff-d1" style={{ padding: '18px 22px', marginBottom: '24px', border: '1px solid rgba(59, 130, 246, 0.3)', position: 'relative' }}>
            <button
              onClick={dismissNotice}
              aria-label="Dismiss notice"
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                color: '#5b6a85',
                cursor: 'pointer',
                padding: '6px',
                lineHeight: 0
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#e2e8f0' }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#5b6a85' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <p style={{ color: '#e2e8f0', fontSize: '13.5px', fontWeight: 600, margin: '0 0 5px 0', letterSpacing: '-0.1px', paddingRight: '24px' }}>
              Our affiliate program has moved from PromoteKit and is now run in house
            </p>
            <p style={{ color: '#94a3b8', fontSize: '12.5px', lineHeight: 1.65, margin: 0 }}>
              We've moved off PromoteKit. If you were already an affiliate, nothing changes for you:
              your existing links keep working, your referred customers and history have been carried over,
              and you still earn 30% recurring. New affiliates can join right here.
            </p>
          </div>
        )}

        {/* Hero + how-it-works: shown to everyone except active affiliates */}
        {!isActiveAffiliate && (<>
        <div className="aff-fade aff-d2" style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.4px', marginBottom: '14px' }}>
            Affiliate program
          </div>
          <h1 style={{
            color: '#f1f5f9',
            fontSize: isMobile ? '30px' : '42px',
            fontWeight: 700,
            margin: '0 auto 16px',
            letterSpacing: '-1px',
            lineHeight: 1.15,
            maxWidth: '640px'
          }}>
            Earn <span className="aff-grad-text">30% recurring</span> commission
          </h1>
          <p style={{ color: '#94a3b8', fontSize: isMobile ? '15px' : '16px', lineHeight: 1.7, margin: '0 auto 22px', maxWidth: '580px' }}>
            The Bookie Charity affiliate program pays 30% of every payment your referrals make,
            for the lifetime of their subscription.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <span className="aff-chip"><CheckIcon size={13} color="#4ade80" /> Instant join</span>
            <span className="aff-chip"><CheckIcon size={13} color="#4ade80" /> 90 day attribution</span>
            <span className="aff-chip"><CheckIcon size={13} color="#4ade80" /> Lifetime of the subscription</span>
          </div>

          {/* Earnings slider */}
          <div className="aff-glass" style={{ maxWidth: '560px', margin: '32px auto 0', padding: '22px 24px', textAlign: 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '16px' }}>
              <span style={{ color: '#e2e8f0', fontSize: '13.5px', fontWeight: 600, letterSpacing: '-0.1px' }}>What could you earn?</span>
              <span style={{ color: '#7c8db0', fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>
                {calcRefs} referred subscriber{calcRefs === 1 ? '' : 's'}
              </span>
            </div>
            <input
              className="aff-range"
              type="range"
              min={1}
              max={25}
              value={calcRefs}
              onChange={(e) => setCalcRefs(Number(e.target.value))}
              style={{ '--fill': `${((calcRefs - 1) / 24) * 100}%` }}
              aria-label="Number of referred subscribers"
            />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px', marginTop: '16px', flexWrap: 'wrap' }}>
              <span className="aff-grad-text" style={{ fontSize: '30px', fontWeight: 700, letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums' }}>
                ${Math.round(calcRefs * 34.99 * (52 / 12) * 0.30)}
              </span>
              <span style={{ color: '#94a3b8', fontSize: '13px' }}>
                a month, for as long as they stay subscribed
              </span>
            </div>
            <p style={{ color: '#42506a', fontSize: '11.5px', margin: '10px 0 0 0' }}>
              Based on Full Access at $34.99 a week, paid on every weekly renewal.
            </p>
          </div>
        </div>

        {/* How it works */}
        <div className="aff-fade aff-d3" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '36px'
        }}>
          {steps.map((s, i) => (
            <div key={i} className="aff-glass aff-step" style={{ padding: '24px' }}>
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '11px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                {s.icon}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '7px' }}>
                <span style={{ color: '#42506a', fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>0{i + 1}</span>
                <span style={{ color: '#f1f5f9', fontSize: '15px', fontWeight: 600, letterSpacing: '-0.2px' }}>{s.title}</span>
              </div>
              <p style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.65, margin: 0 }}>{s.text}</p>
            </div>
          ))}
        </div>
        </>)}

        {/* Logged in, not yet an affiliate — join form */}
        {user && loaded && !affiliate && (
          <div className="aff-glass aff-fade aff-d4" style={{ padding: '28px 24px', marginBottom: '36px' }}>
            <p style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: 600, margin: '0 0 4px 0', letterSpacing: '-0.2px' }}>
              Get my referral link
            </p>
            <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 22px 0' }}>
              Pick a code and your link is live immediately.
            </p>
            <form onSubmit={handleJoin}>
              <div style={{ marginBottom: '18px', maxWidth: isMobile ? '100%' : '360px' }}>
                <label style={labelStyle}>Referral code</label>
                <input
                  className="aff-input"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="yourcode"
                  maxLength={20}
                />
                <div className="aff-mono" style={{ color: '#42506a', fontSize: '11.5px', marginTop: '8px' }}>
                  app.bookiecharity.com/?via={code.trim().toLowerCase() || 'yourcode'}
                </div>
              </div>
              {formError && (
                <div style={{ color: '#f87171', fontSize: '13px', marginBottom: '14px' }}>
                  {formError}
                </div>
              )}
              <button
                className="aff-btn aff-btn-primary"
                type="submit"
                disabled={submitting}
                style={{ padding: '11px 24px', fontSize: '14px', opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
              >
                {submitting ? 'Creating…' : 'Get my referral link'}
              </button>
            </form>
          </div>
        )}

        {/* Disabled notice */}
        {user && affiliate && affiliate.status === 'disabled' && (
          <div className="aff-glass aff-fade aff-d2" style={{ padding: '24px', marginBottom: '36px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            <p style={{ color: '#f1f5f9', fontSize: '15px', fontWeight: 600, margin: '0 0 6px 0' }}>
              Your affiliate account is disabled
            </p>
            <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
              This account is no longer earning commissions. If you think this is a mistake,
              contact <a href="mailto:support@bookiecharity.com" style={{ color: '#60a5fa', textDecoration: 'none' }}>support@bookiecharity.com</a>.
            </p>
          </div>
        )}

        {/* Affiliate dashboard */}
        {isActiveAffiliate && (
          <div className="aff-fade aff-d2" style={{ marginBottom: '36px' }}>

            {/* Dashboard header */}
            <div style={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'flex-end',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: '14px',
              marginBottom: '24px'
            }}>
              <div>
                <div style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: '8px' }}>
                  Affiliate program
                </div>
                <h1 style={{ color: '#f1f5f9', fontSize: '26px', fontWeight: 650, margin: 0, letterSpacing: '-0.4px' }}>
                  Your dashboard
                </h1>
                <p style={{ color: '#5b6a85', fontSize: '13px', margin: '6px 0 0 0' }}>
                  Share your link, track referrals, get paid monthly.
                </p>
              </div>
              <span className="aff-chip">
                Payday · 15th of each month
              </span>
            </div>

            {/* Stats band */}
            <div className="aff-glass aff-kpis" style={{ marginBottom: '14px', overflow: 'hidden' }}>
              {kpis.map((k) => (
                <div key={k.label} className="aff-kpi">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: k.dot, flexShrink: 0 }} />
                    <span style={{ color: '#5b6a85', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.7px', whiteSpace: 'nowrap' }}>
                      {k.label}
                    </span>
                  </div>
                  <div style={{ color: '#f1f5f9', fontSize: '23px', fontWeight: 650, letterSpacing: '-0.6px', fontVariantNumeric: 'tabular-nums' }}>
                    {k.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Earnings chart — only once there's something to show */}
            {hasChartData && (
              <div className="aff-glass" style={{ padding: '24px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
                  <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600, letterSpacing: '-0.2px' }}>Earnings</span>
                  <span style={{ color: '#42506a', fontSize: '12px' }}>last 12 months</span>
                </div>
                <div style={{ marginTop: '18px' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartBuckets} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255, 255, 255, 0.045)" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        fontSize={10.5}
                        tick={{ fill: '#4d5b74' }}
                        dy={6}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        fontSize={10.5}
                        tick={{ fill: '#4d5b74' }}
                        tickFormatter={(v) => '$' + v}
                        width={52}
                        allowDecimals={false}
                      />
                      <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255, 255, 255, 0.12)', strokeWidth: 1 }} />
                      <Line
                        type="monotone"
                        dataKey="earnings"
                        stroke={CHART_EARNINGS}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 0 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Referral link — gradient hairline card */}
            <div style={{
              background: 'linear-gradient(120deg, rgba(59, 130, 246, 0.55), rgba(168, 85, 247, 0.3) 45%, rgba(255, 255, 255, 0.07) 75%)',
              borderRadius: '17px',
              padding: '1px',
              marginBottom: '14px'
            }}>
              <div style={{
                background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(11, 17, 32, 0.98))',
                borderRadius: '16px',
                padding: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                  <LinkIcon size={15} color="#60a5fa" />
                  <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600, letterSpacing: '-0.2px' }}>Your referral link</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexWrap: 'wrap',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: '11px',
                  padding: '13px 16px'
                }}>
                  <code className="aff-mono" style={{ color: '#cbd5e1', flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                    {referralLink}
                  </code>
                  <button
                    className="aff-btn"
                    onClick={handleCopy}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '7px',
                      background: copied ? 'rgba(34, 197, 94, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${copied ? 'rgba(34, 197, 94, 0.35)' : 'rgba(255, 255, 255, 0.12)'}`,
                      borderRadius: '8px',
                      color: copied ? '#4ade80' : '#94a3b8',
                      padding: '8px 14px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}
                  >
                    {copied ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            {/* Payout details */}
            <div className="aff-glass" style={{
              padding: '24px',
              marginBottom: '14px',
              border: needsPayoutDetails ? '1px solid rgba(245, 158, 11, 0.3)' : undefined
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <BanknoteIcon size={15} color="#7c8db0" />
                <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600, letterSpacing: '-0.2px' }}>Payout details</span>
                {payoutSaved && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#4ade80', fontSize: '12px', fontWeight: 500 }}>
                    <CheckIcon size={12} /> Saved
                  </span>
                )}
              </div>
              <p style={{ color: needsPayoutDetails ? '#fbbf24' : '#42506a', fontSize: '12.5px', margin: '0 0 18px 0' }}>
                {needsPayoutDetails
                  ? 'You have earnings but no payout details. Add them so we can pay you.'
                  : 'How we send your commissions. You can change this any time.'}
              </p>

              <form onSubmit={handleSavePayout}>
                <div className="aff-seg" style={{ marginBottom: '18px' }}>
                  <button type="button" className={payoutMethod === 'paypal' ? 'on' : ''} onClick={() => { setPayoutMethod('paypal'); setPayoutError('') }}>
                    PayPal
                  </button>
                  <button type="button" className={payoutMethod === 'bank_au' ? 'on' : ''} onClick={() => { setPayoutMethod('bank_au'); setPayoutError('') }}>
                    Bank transfer (Australia)
                  </button>
                </div>

                {payoutMethod === 'paypal' ? (
                  <div style={{ marginBottom: '18px', maxWidth: isMobile ? '100%' : '340px' }}>
                    <label style={labelStyle}>PayPal email</label>
                    <input
                      className="aff-input"
                      type="email"
                      value={payoutEmail}
                      onChange={(e) => setPayoutEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                ) : (
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '160px 230px',
                      gap: '14px',
                      marginBottom: '10px'
                    }}>
                      <div>
                        <label style={labelStyle}>BSB</label>
                        <input
                          className="aff-input"
                          type="text"
                          inputMode="numeric"
                          value={payoutBsb}
                          onChange={(e) => setPayoutBsb(e.target.value)}
                          placeholder="062-000"
                          maxLength={7}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Account number</label>
                        <input
                          className="aff-input"
                          type="text"
                          inputMode="numeric"
                          value={payoutAccount}
                          onChange={(e) => setPayoutAccount(e.target.value)}
                          placeholder="12345678"
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div style={{ color: '#42506a', fontSize: '11.5px', marginBottom: '18px' }}>
                      Bank transfer is for Australian accounts only.
                    </div>
                  </>
                )}

                {payoutError && (
                  <div style={{ color: '#f87171', fontSize: '13px', marginBottom: '14px' }}>
                    {payoutError}
                  </div>
                )}

                <button
                  className="aff-btn"
                  type="submit"
                  disabled={savingPayout}
                  style={{
                    background: 'rgba(59, 130, 246, 0.12)',
                    border: '1px solid rgba(59, 130, 246, 0.35)',
                    borderRadius: '9px',
                    color: '#60a5fa',
                    padding: '9px 20px',
                    fontSize: '13px',
                    fontWeight: 600,
                    opacity: savingPayout ? 0.6 : 1,
                    cursor: savingPayout ? 'not-allowed' : 'pointer'
                  }}
                >
                  {savingPayout ? 'Saving…' : 'Save payout details'}
                </button>
              </form>
            </div>

            {/* Commissions */}
            <div className="aff-glass" style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
                <span style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 600, letterSpacing: '-0.2px' }}>Commissions</span>
                {commissions.length > 0 && (
                  <span style={{ color: '#42506a', fontSize: '12px' }}>{commissions.length} total</span>
                )}
              </div>
              {commissions.length > 0 ? (
                <div className="aff-table-wrap">
                  <table className="aff-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="num">Payment</th>
                        <th className="num">Commission</th>
                        <th>Status</th>
                        <th>Matures</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((c) => {
                        const status = displayStatus(c)
                        return (
                          <tr key={c.id}>
                            <td style={{ color: '#5b6a85' }}>{formatDate(c.created_at)}</td>
                            <td className="num">{formatMoney(c.amount_paid_cents)}</td>
                            <td className="num" style={{ color: '#f1f5f9', fontWeight: 600 }}>{formatMoney(c.commission_cents)}</td>
                            <td><Pill status={status} /></td>
                            <td style={{ color: '#5b6a85' }}>{formatDate(c.matures_at)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#42506a', fontSize: '13px', margin: '14px 0 0 0', lineHeight: 1.6 }}>
                  No commissions yet. Share your link to start earning.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Program terms */}
        <div className="aff-glass aff-fade aff-d4" style={{ padding: '28px 24px' }}>
          <h2 style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: 600, margin: '0 0 20px 0', letterSpacing: '-0.2px' }}>
            Program terms
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '16px 28px'
          }}>
            {terms.map((t, i) => (
              <div key={i} className="aff-term">
                <span style={{ marginTop: '2px' }}><CheckIcon size={13} color="#4ade80" /></span>
                <p style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.65, margin: 0 }}>{t}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}
